import { Dayjs } from "dayjs"
import { useState } from "react"

import {
    AssessEVACDeviceDetails,
    AssessEVDCDeviceDetails,
} from "../../../../components/devices/lct/types/providerTypes"
import { Datasets } from "../../../../constants/enums"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import {
    dayJsToStrForTtrApi,
    snackbar,
} from "../../../../utils/general/generalUtils"
import { scrollToTop } from "../../../../utils/submitApplication/submitApplicationUtils"
import { postClientProtected } from "../../clients/clients"
import { LCT_ASSESS_DEVICE, TTR_ASSESS_DEVICE } from "../../urls/private"

interface IAssessDeviceHook {
    isSending: boolean
    error: IAppError | undefined
    postTtrResponse: (
        deviceRef: string,
        basedOnPublishedDate: Dayjs,
        complianceStatusID: number
    ) => Promise<void>
    postLctResponse: (
        deviceRef: string,
        basedOnPublishedDate: Dayjs,
        dataset: Datasets,
        body: AssessEVACDeviceDetails | AssessEVDCDeviceDetails
    ) => Promise<void>
}

/**
 * Provides functions and logic for posting a submit assessment request
 * on a TTR device.
 * @returns {IAssessDeviceHook} - loading and error states.
 */
const useAssessDevice = (onSuccess: () => void): IAssessDeviceHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)

    const postTtrResponse = async (
        deviceRef: string,
        basedOnPublishedDate: Dayjs,
        complianceStatusID: number
    ): Promise<void> => {
        try {
            setIsSending(true)

            // Build response
            const body = {
                DeviceRef: deviceRef,
                BasedOnPublishedDate: dayJsToStrForTtrApi(basedOnPublishedDate),
                ComplianceStatusID: complianceStatusID,
            }
            await postClientProtected(TTR_ASSESS_DEVICE, body)
            snackbar("Assessment submitted successfully", "success")
            setError(undefined)
            onSuccess()
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    const postLctResponse = async (
        deviceRef: string,
        basedOnPublishedDate: Dayjs,
        dataset: Datasets,
        assessedDetails: AssessEVACDeviceDetails | AssessEVDCDeviceDetails
    ): Promise<void> => {
        try {
            setIsSending(true)
            // Build response
            const body = {
                DeviceRef: deviceRef,
                BasedOnPublishedDate: dayJsToStrForTtrApi(basedOnPublishedDate),
                Dataset: dataset,
                ...assessedDetails,
            }
            await postClientProtected(LCT_ASSESS_DEVICE, body)
            snackbar("Assessment submitted successfully", "success")
            setError(undefined)
            onSuccess()
            scrollToTop(document)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    return {
        error,
        isSending,
        postTtrResponse,
        postLctResponse,
    }
}

export default useAssessDevice
